import { MGPFallible, MGPOptional, MGPValidation, Utils } from '@everyboard/lib';
import { GameNode } from '../../jscaip/AI/GameNode';
import { GameStatus } from '../../jscaip/GameStatus';
import { Player } from '../../jscaip/Player';
import { Rules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { MartianChessFailure } from './MartianChessFailure';
import { MartianChessMoveFailure } from './MartianChessMove';
import { MartianChessPiece } from './MartianChessPiece';
import { MartianChessState } from './MartianChessState';
export class MartianChessNode extends GameNode {
}
export class MartianChessRules extends Rules {
    static STARTING_COUNT_DOWN = MGPOptional.of(7);
    static singleton = MGPOptional.empty();
    static get() {
        if (MartianChessRules.singleton.isAbsent()) {
            MartianChessRules.singleton = MGPOptional.of(new MartianChessRules());
        }
        return MartianChessRules.singleton.get();
    }
    getInitialState() {
        const _ = MartianChessPiece.EMPTY;
        const A = MartianChessPiece.PAWN;
        const B = MartianChessPiece.DRONE;
        const C = MartianChessPiece.QUEEN;
        const board = [
            [C, C, B, _],
            [C, B, A, _],
            [B, A, A, _],
            [_, _, _, _],
            [_, _, _, _],
            [_, A, A, B],
            [_, A, B, C],
            [_, B, C, C],
        ];
        return new MartianChessState(board, 0, MGPOptional.empty());
    }
    applyLegalMove(move, state, _config, info) {
        const newBoard = state.getCopiedBoard();
        newBoard[move.getStart().y][move.getStart().x] = MartianChessPiece.EMPTY;
        const landingPiece = info.finalPiece;
        newBoard[move.getEnd().y][move.getEnd().x] = landingPiece;
        const captured = info.score;
        let countDown = state.countDown;
        if (countDown.isPresent()) {
            const isCapture = this.isCapture(move, state);
            if (isCapture) {
                countDown = MartianChessRules.STARTING_COUNT_DOWN;
            }
            else {
                const previousRemainingTurn = state.countDown.get();
                countDown = MGPOptional.of(previousRemainingTurn - 1);
            }
        }
        if (move.calledTheClock) {
            countDown = MartianChessRules.STARTING_COUNT_DOWN;
        }
        return new MartianChessState(newBoard, state.turn + 1, MGPOptional.of(move), countDown, captured);
    }
    isLegal(move, state) {
        this.assertNonDoubleClockCall(move, state);
        const moveLegality = this.isLegalMove(move, state);
        if (moveLegality.isFailure()) {
            return moveLegality.toOtherFallible();
        }
        if (move.isUndoneBy(state.lastMove)) {
            return MGPFallible.failure(MartianChessFailure.CANNOT_UNDO_LAST_MOVE());
        }
        if (this.isFieldPromotion(move, state)) {
            return this.isLegalFieldPromotion(move, state);
        }
        const landingPiece = state.getPieceAt(move.getStart());
        const captured = state.captured.getCopy();
        if (this.isCapture(move, state)) {
            const currentPlayer = state.getCurrentPlayer();
            let playerScore = captured.get(currentPlayer).get();
            const capturedPiece = state.getPieceAt(move.getEnd());
            playerScore = playerScore.add(capturedPiece);
            captured.replace(currentPlayer, playerScore);
            captured.makeImmutable();
        }
        const moveResult = { finalPiece: landingPiece, score: captured };
        return MGPFallible.success(moveResult);
    }
    assertNonDoubleClockCall(move, state) {
        const clockHadAlreadyBeenCalled = state.countDown.isPresent();
        const clockCalledThisTurn = move.calledTheClock;
        const doubleClockCall = clockHadAlreadyBeenCalled && clockCalledThisTurn;
        Utils.assert(doubleClockCall === false, 'Should not call the clock twice');
    }
    isCapture(move, state) {
        const moveEndsInOpponentTerritory = state.isInOpponentTerritory(move.getEnd());
        const moveEndsOnPiece = state.getPieceAt(move.getEnd()) !== MartianChessPiece.EMPTY;
        return moveEndsInOpponentTerritory && moveEndsOnPiece;
    }
    isFieldPromotion(move, state) {
        const moveEndsInPlayerTerritory = state.isInPlayerTerritory(move.getEnd());
        const moveEndsOnPiece = state.getPieceAt(move.getEnd()) !== MartianChessPiece.EMPTY;
        return moveEndsInPlayerTerritory && moveEndsOnPiece;
    }
    isLegalFieldPromotion(move, state) {
        const optCreatedPiece = this.getPromotedPiece(move, state);
        if (optCreatedPiece.isAbsent()) {
            return MGPFallible.failure(MartianChessFailure.CANNOT_CAPTURE_YOUR_OWN_PIECE_NOR_PROMOTE_IT());
        }
        const createdPiece = optCreatedPiece.get();
        if (state.isTherePieceOnPlayerSide(createdPiece)) {
            return MGPFallible.failure(MartianChessFailure.CANNOT_CAPTURE_YOUR_OWN_PIECE_NOR_PROMOTE_IT());
        }
        else {
            const moveResult = {
                finalPiece: createdPiece,
                score: state.captured.getCopy(),
            };
            return MGPFallible.success(moveResult);
        }
    }
    isLegalMove(move, state) {
        const moveStartsInPlayerTerritory = state.isInPlayerTerritory(move.getStart());
        if (moveStartsInPlayerTerritory === false) {
            return MGPValidation.failure(MartianChessFailure.MUST_CHOOSE_PIECE_FROM_YOUR_TERRITORY());
        }
        const movedPiece = state.getPieceAt(move.getStart());
        if (movedPiece === MartianChessPiece.EMPTY) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        else if (movedPiece === MartianChessPiece.PAWN) {
            if (move.isValidForPawn()) {
                return MGPValidation.SUCCESS;
            }
            else {
                return MGPValidation.failure(MartianChessMoveFailure.PAWN_MUST_MOVE_ONE_DIAGONAL_STEP());
            }
        }
        else if (movedPiece === MartianChessPiece.DRONE) {
            if (move.isValidForDrone() === false) {
                return MGPValidation.failure(MartianChessMoveFailure.DRONE_MUST_DO_TWO_ORTHOGONAL_STEPS());
            }
        }
        for (const coord of move.getJumpedOverCoords()) {
            if (state.getPieceAt(coord) !== MartianChessPiece.EMPTY) {
                return MGPValidation.failure(RulesFailure.SOMETHING_IN_THE_WAY());
            }
        }
        return MGPValidation.SUCCESS;
    }
    getPromotedPiece(move, state) {
        const startPiece = state.getPieceAt(move.getStart());
        const endPiece = state.getPieceAt(move.getEnd());
        return MartianChessPiece.tryMerge(startPiece, endPiece);
    }
    getGameStatus(node) {
        const state = node.gameState;
        if (state.countDown.equalsValue(0)) {
            return this.getGameStatusScoreVictoryOr(state, GameStatus.DRAW);
        }
        const emptyTerritory = state.getEmptyTerritory();
        if (emptyTerritory.isPresent()) {
            const previousPlayer = state.getPreviousPlayer();
            const previousPlayerVictoryStatus = GameStatus.getVictory(previousPlayer);
            return this.getGameStatusScoreVictoryOr(state, previousPlayerVictoryStatus);
        }
        return GameStatus.ONGOING;
    }
    getGameStatusScoreVictoryOr(state, defaultStatus) {
        const scoreZero = state.getScoreOf(Player.ZERO);
        const scoreOne = state.getScoreOf(Player.ONE);
        if (scoreZero > scoreOne)
            return GameStatus.ZERO_WON;
        else if (scoreOne > scoreZero)
            return GameStatus.ONE_WON;
        else
            return defaultStatus;
    }
}
